import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as DwhIcon } from '@elementary/common-ui/assets/dwh.svg';
import {
  Flex,
  FlexCenter,
  FlexColumn,
  SearchField,
} from '@elementary/common-ui/components';
import { Typography, Divider } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { DWH } from '@elementary/common-ui/constants/queryKeys';
import { UseFormWatch } from 'react-hook-form';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import * as api from 'shared/services/api';
import { AddTestForm, ColumnResource, Resource } from 'shared/types';

const Wrapper = styled(FlexColumn)`
  position: absolute;
  top: calc(100% + 1px);
  left: -1px;
  z-index: 5;
  gap: 16px;
  background: white;
  border: 1px solid ${({ theme }) => theme.palette.common.lightBlue3};
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 0px 0px 8px 8px;
  border-top: none;
  padding: 24px 0;
  max-height: 500px;
  cursor: default;
`;

const DwhChip = styled(FlexCenter)`
  background: ${({ theme }) => theme.palette.common.gray10};
  padding: 8px;
  border-radius: 8px;
  gap: 8px;
  width: fit-content;
`;

type Props = {
  watch: UseFormWatch<AddTestForm>;
  tableResourcesData: Resource[];
  columnsResourcesData: ColumnResource[];
};

const StepperTargetPopup = ({
  watch,
  tableResourcesData,
  columnsResourcesData,
}: Props) => {
  const selectedEnvId = useSelectedEnv();

  const { data: dwhData } = useQuery(
    [DWH, selectedEnvId],
    () => api.fetchDwh(selectedEnvId!),
    { enabled: !!selectedEnvId },
  );

  const [search, setSearch] = useState('');

  const selectedResources = watch('resources');

  const resourceType = watch('resourceType');

  const listData: (Resource | ColumnResource)[] = useMemo(
    () =>
      resourceType === 'tables' ? tableResourcesData : columnsResourcesData,
    [columnsResourcesData, resourceType, tableResourcesData],
  );

  const filteredResources = useMemo(
    () =>
      search.trim()
        ? listData
            .filter((r) => selectedResources.includes(r.id))
            .filter(
              (r) =>
                r.name.toLowerCase().includes(search.toLowerCase()) ||
                r.schema.toLowerCase().includes(search.toLowerCase()) ||
                (r as ColumnResource).model_name
                  ?.toLowerCase()
                  .includes(search.toLowerCase()),
            )
        : listData.filter((r) => selectedResources.includes(r.id)),
    [listData, search, selectedResources],
  );

  return (
    <Wrapper>
      <Flex sx={{ px: 3, pointerEvents: 'none', userSelect: 'none' }}>
        <DwhChip>
          <DwhIcon />
          <Typography sx={{ color: 'common.gray2' }}>
            {dwhData?.database}
          </Typography>
        </DwhChip>
      </Flex>

      <Divider />

      <SearchField
        sx={{ px: 3 }}
        onChange={(e) => setSearch(e.target.value)}
        value={search}
        fullWidth
      />

      <FlexColumn
        sx={{ overflow: 'auto', height: '100%', maxHeight: '100%', pt: 1 }}
      >
        <FlexColumn
          sx={{
            gap: 2,
            px: 3,
          }}
        >
          {filteredResources?.map((r) => (
            <Flex key={r.id}>
              <Typography
                sx={{ color: 'common.gray2' }}
              >{`${r.schema?.toLowerCase()}.`}</Typography>
              {resourceType === 'columns' && (
                <Typography sx={{ color: 'common.gray2' }}>{`${(
                  r as ColumnResource
                ).model_name?.toLowerCase()}.`}</Typography>
              )}
              <Typography>{`${r.name?.toLowerCase()}`}</Typography>
            </Flex>
          ))}
        </FlexColumn>
      </FlexColumn>
    </Wrapper>
  );
};

export default StepperTargetPopup;
